import React, { useMemo, useState } from 'react';
import { PetType } from '@spike/model';
import { Service, ServicePricing, TransportationPricing, FixedPriceOption } from 'model/Service';
import useOrganizationMarketplaces from 'hooks/useOrganizationMarketplaces';
import { buildPricingTaxesFromMarketplaces } from 'components/Service/Pricing/utils/taxElements';
import Button from 'components/UI/V3/Button/Button';
import { TransportationTaxesSelector } from './Sections/Taxes/TransportationTaxesSelector';
import { TransportationPriceFields } from './Sections/PriceFields/TransportationPriceFields';
import styles from './TransportationPricingStep.module.scss';

interface TransportationPricingStepProps {
    selectedPetTypes: Array<PetType>;
    marketplaces: Service['marketplaces'];
    existingService?: Service;
    onNext: (pricing: ServicePricing, marketplaces: Service['marketplaces']) => void;
}

export const TransportationPricingStep: React.FC<TransportationPricingStepProps> = ({
    selectedPetTypes,
    marketplaces: initialMarketplaces,
    existingService,
    onNext
}) => {
    const { organizationServicesInfo } = useOrganizationMarketplaces();

    const [taxable, setTaxable] = useState(
        () =>
            existingService?.pricing?.taxable ||
            initialMarketplaces.some(marketplace => marketplace.taxes.length > 0) ||
            false
    );
    const [marketplaces, setMarketplaces] = useState<Service['marketplaces']>(() => initialMarketplaces);

    const [transportationPrices, setTransportationPrices] = useState<TransportationPricing>(() => {
        const firstPetPrice = existingService?.pricing?.fixedPrice?.[0];
        return (
            firstPetPrice?.transportationPrice || {
                pickUpPrice: 0,
                dropOffPrice: 0
            }
        );
    });

    const [priceErrors, setPriceErrors] = useState<{ pickUp?: string; dropOff?: string }>({});

    React.useEffect(() => {
        setMarketplaces(prev =>
            initialMarketplaces.map(marketplace => {
                const existing = prev.find(mp => mp.marketplaceId === marketplace.marketplaceId);
                return {
                    ...marketplace,
                    taxes: existing?.taxes ?? marketplace.taxes,
                    staffIds: existing?.staffIds ?? marketplace.staffIds,
                    roomIds: marketplace.roomIds ?? existing?.roomIds ?? [],
                    roomUnitIds: marketplace.roomUnitIds ?? existing?.roomUnitIds ?? [],
                    vehicleIds: marketplace.vehicleIds ?? existing?.vehicleIds ?? []
                };
            })
        );
    }, [initialMarketplaces]);

    const taxes = useMemo(
        () => buildPricingTaxesFromMarketplaces(marketplaces, organizationServicesInfo),
        [marketplaces, organizationServicesInfo]
    );

    const isValid = useMemo(() => {
        if (
            !(transportationPrices.pickUpPrice >= 0) ||
            !(transportationPrices.dropOffPrice >= 0) ||
            Object.keys(priceErrors).length > 0
        ) {
            return false;
        }

        if (taxable && taxes.length === 0) {
            return false;
        }

        return true;
    }, [transportationPrices, priceErrors, taxable, taxes]);

    const handleNext = () => {
        if (!isValid) return;

        const fixedPriceArray: Array<FixedPriceOption> = selectedPetTypes.map(petType => ({
            petType,
            transportationPrice: {
                pickUpPrice: transportationPrices.pickUpPrice,
                dropOffPrice: transportationPrices.dropOffPrice
            }
        }));

        const pricing: ServicePricing = {
            taxable,
            taxes,
            fixedPrice: fixedPriceArray
        };

        onNext(pricing, marketplaces);
    };

    return (
        <div className={styles.container}>
            <div className={styles.content}>
                <div className={styles.section}>
                    <TransportationPriceFields
                        prices={transportationPrices}
                        onPricesChange={setTransportationPrices}
                        onErrorsChange={setPriceErrors}
                    />
                </div>

                <div className={styles.section}>
                    <TransportationTaxesSelector
                        taxable={taxable}
                        marketplaces={marketplaces}
                        onTaxableChange={setTaxable}
                        onChangeMarketplaces={setMarketplaces}
                    />
                </div>
            </div>

            <div className={styles.buttonContainer}>
                <Button
                    variant="primary"
                    size="large"
                    label="Next"
                    onClick={handleNext}
                    disabled={!isValid}
                />
            </div>
        </div>
    );
};
